import { styled } from '@mui/material/styles';
import { COLOR } from '@src/styles/color';
import { FADE_IN_UP, FADE_IN_UP_DURATION } from '@src/styles/animation';
import { ReactComponent as WarningRedIcon } from '@src/assets/icons/waning-red.svg';

// Sized here rather than in each container, so the list and the grid match.
export const StyledFailureWarning = styled(WarningRedIcon)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

export const StyledRecentProjects = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* Its own 32px row, as in the design, so the 16px gap below it puts the
     toolbar where the mock has it. */
  .recent-title {
    display: flex;
    align-items: center;
    height: 32px;
  }

  .recent-toolbar {
    display: flex;
    align-items: center;
    /* On the bar itself rather than on one of its two states: the selection
       controls are 32px tall against the search row's 40px, and a bar that
       takes its height from them pulls the cards below up by 8px on the swap. */
    height: 40px;
    box-sizing: border-box;
    gap: 12px;
  }

  /* The selection bar is inset, so its checkbox lines up with the one on the
     first card. The search row keeps the full width. */
  .recent-toolbar-selection {
    padding: 0 8px;
    gap: 8px;
  }

  .recent-search {
    flex: 1;
    min-width: 0;

    .MuiOutlinedInput-root {
      height: 40px;
      border-radius: 8px;
      background-color: ${COLOR.white};
    }

    /* MUI's own outline is a near-black; the design wants the same faint line
       as the buttons beside it. */
    .MuiOutlinedInput-notchedOutline {
      border: 1px solid ${COLOR.secondary[16]};
    }
  }

  .recent-selection {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .recent-select-all {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 8px;
    box-sizing: border-box;
    border: 1px solid ${COLOR.borderDropdown};

    svg {
      width: 18px;
      height: 18px;
      color: ${COLOR.darkBlue};
    }
  }

  .recent-delete {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 32px;
    flex-shrink: 0;
    padding: 4px 8px;
    border-radius: 8px;
    text-transform: none;
    color: ${COLOR.secondary[8]};
    border: 1px solid ${COLOR.borderDropdown};

    /* The gap above is the spacing; MUI's own would add to it. */
    .MuiButton-startIcon {
      margin: 0;
    }
  }

  .recent-views {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .recent-view-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    box-sizing: border-box;
    cursor: pointer;
    border: 1px solid ${COLOR.borderDropdown};
    background-color: ${COLOR.white};
    transition: background-color 0.2s ease, transform 0.15s ease;

    &:active {
      transform: scale(0.9);
    }

    svg {
      width: 24px;
      height: 24px;
      color: ${COLOR.indigo[64]};
      transition: color 0.2s ease;
    }
  }

  .recent-view-button-active {
    background-color: ${COLOR.secondary[40]};

    svg {
      color: ${COLOR.indigo[80]};
    }
  }

  /* NewTable is shared, so its row checkbox keeps the theme's yellow primary.
     Recoloured here rather than there, where it would reach every page. */
  .body-checkbox {
    color: ${COLOR.darkBlue};
  }

  /* The art is 200px and carries an inline height:auto, which a class cannot
     override — max-height is a separate property, so it applies. 100px is what
     the projects page shows it at. Selected by alt: NoData renders it
     classless. */
  img[alt='no data'] {
    max-height: 100px;
  }
`;

export const StyledRowActions = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const StyledRowMenu = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;

  .row-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    /* The column above starts its children, so this is what fills the width. */
    align-self: stretch;
    height: 40px;
    padding: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${COLOR.indigo.default};
    border-radius: 8px;
  }

  .row-menu-icon {
    width: 24px;
    height: 24px;
    color: ${COLOR.indigo[80]};
  }
`;

/* Values copied from the projects page's row (Project/ProjectList/index.style),
   so the two lists read the same. The row border comes from NewTable. */
export const StyledProjectRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 52px;
  cursor: pointer;
  /* Pulls back the cell padding NewTable adds, so the icon sits as close to the
     checkbox as it does on the projects page. */
  margin-left: -12px;

  .row-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  .row-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .row-title {
    font-size: 14px;
    font-weight: 510;
    line-height: 24px;
    color: ${COLOR.label2[100]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .row-date {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${COLOR.label[64]};
  }
`;

export const StyledProjectGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr));
  gap: 16px;

  /* On the children, so a batch loaded by scrolling comes in while the cards
     above it stay put. */
  & > * {
    animation: ${FADE_IN_UP} ${FADE_IN_UP_DURATION} ease-out;
  }
`;

export const StyledCardSkeleton = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* Same box as a real card, so nothing shifts when the data lands. */
  .skeleton-thumbnail {
    height: 196px;
    border-radius: 16px;
  }

  .skeleton-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

export const StyledProjectCard = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  /* Skips paint below the fold, which the blurred backdrop makes worth doing.
     254px is the card's height (196 thumbnail + 8 gap + 50 info). */
  content-visibility: auto;
  contain-intrinsic-size: auto 254px;

  .card-thumbnail {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 196px;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    box-sizing: border-box;
    border-width: 1px;
    border-style: solid;
    border-color: ${COLOR.borderDropdown};
    background-color: ${COLOR.backgroundDefault};
  }

  /* Hidden, and out of reach with it: an invisible checkbox still takes the
     click that was meant to open the project. */
  .card-select,
  .card-actions {
    position: absolute;
    top: 8px;
    opacity: 0;
    pointer-events: none;
  }

  /* The box is on the button itself, so it has no dead ring around it. */
  .card-select,
  .card-actions .MuiIconButton-root {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 4px;
    box-sizing: border-box;
    flex-shrink: 0;
    border-radius: 8px;
    border: 1px solid ${COLOR.secondary[16]};
    background-color: ${COLOR.white};

    /* MUI paints its own tint on hover, which would wash the white pad out. */
    &:hover {
      background-color: ${COLOR.white};
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .card-select {
    left: 8px;

    /* The menu icon takes its colour from RowActions, which the list shares. */
    svg {
      color: ${COLOR.darkBlue};
    }
  }

  .card-actions {
    right: 8px;
  }

  .card-thumbnail:hover .card-select,
  .card-thumbnail:hover .card-actions,
  &.card-armed .card-select,
  &.card-armed .card-actions {
    opacity: 1;
    pointer-events: auto;
  }

  .card-backdrop {
    position: absolute;
    inset: 0;
  }

  .card-backdrop-media {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    filter: blur(16px);
    /* Blur pulls in whatever lies past the edges; the scale pushes those
       washed-out borders outside the thumbnail. */
    transform: scale(1.15);
  }

  .card-media {
    /* Above the backdrop: both are positioned, and this one comes later. */
    position: relative;
    width: 100%;
    height: 100%;
    /* contain, not cover: a 16:9 frame in a squarer card would lose its edges. */
    object-fit: contain;
    display: block;
  }

  /* Pushed down and tilted, like a sheet dropped on a desk. The text is
     inside, so it leans with the paper. */
  .card-paper {
    position: relative;
    height: 195px;
    transform: translateY(18%) rotate(4deg);
  }

  /* Height drives the size, so the sheet keeps its own proportions. */
  .card-paper-image {
    height: 100%;
    width: auto;
    display: block;
  }

  /* Starts near the top of the sheet, level with the folded corner. */
  .card-paper-text {
    position: absolute;
    inset: 10% 20% 16% 20%;
    overflow: hidden;
    font-size: 10.5px;
    line-height: 15px;
    letter-spacing: -0.3px;
    color: ${COLOR.label2[48]};
  }

  /* One line tall, the height of the fold itself. */
  .card-paper-fold {
    float: right;
    width: 10%;
    height: 15px;
  }

  .card-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    padding: 4px 8px;
    box-sizing: border-box;
  }

  .card-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .card-type-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  /* Takes the space left over, so the warning sits against the right edge. */
  .card-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
